<template>
    <div>
        <custom-table
            :title="$t('pages.ecommerce.product.dependency.required_title')"
            :subTitle="$t('pages.ecommerce.product.dependency.required_subtitle')"
            rowKey="id"
            :items="andDependencies"
            :loading="table.loading"
            selectableRows
            :emptyTableText="$t('pages.ecommerce.product.dependency.no_required')"
            :columns="fields"
            :actions="tableActions"
            @action="(name) => handleClickAction(name, 1)"
            @selectableRows="handleAndSelected">
            <template v-slot:product_name="{ row: record }">
                {{ record.depends_on?.name }}
                <span v-if="record.depends_on?.code" class="text-muted">({{ record.depends_on.code }})</span>
            </template>
            <template v-slot:actions="{ row: record }">
                <div class="d-flex justify-content-end">
                    <a @click="deleteDependency(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                        </span>
                    </a>
                </div>
            </template>
        </custom-table>

        <div class="mt-5"></div>

        <custom-table
            :title="$t('pages.ecommerce.product.dependency.alternative_title')"
            :subTitle="$t('pages.ecommerce.product.dependency.alternative_subtitle')"
            rowKey="id"
            :items="orDependencies"
            :loading="table.loading"
            selectableRows
            :emptyTableText="$t('pages.ecommerce.product.dependency.no_alternative')"
            :columns="fields"
            :actions="tableActions"
            @action="(name) => handleClickAction(name, 2)"
            @selectableRows="handleOrSelected">
            <template v-slot:product_name="{ row: record }">
                {{ record.depends_on?.name }}
                <span v-if="record.depends_on?.code" class="text-muted">({{ record.depends_on.code }})</span>
            </template>
            <template v-slot:actions="{ row: record }">
                <div class="d-flex justify-content-end">
                    <a @click="deleteDependency(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                        </span>
                    </a>
                </div>
            </template>
        </custom-table>

        <div class="modal fade" id="kt_modal_product_dependency" ref="depModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered mw-650px">
                <div class="modal-content">
                    <div class="modal-header">
                        <h2 class="fw-bolder">{{ form.title }}</h2>
                        <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                            <span class="svg-icon svg-icon-1">
                                <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                            </span>
                        </div>
                    </div>
                    <el-form @submit.prevent="onSubmit()" :model="form.data" ref="depForm">
                        <div class="modal-body py-10 px-lg-17">
                            <div class="fv-row mb-1 mt-3">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.product.dependency.cols.product_name') }}</label>
                                <el-form-item prop="depends_on_product_id" :rules="$validation.getMessage(['required'])">
                                    <el-select v-model="form.data.depends_on_product_id" filterable class="w-100" :placeholder="$t('common.chooseSelect')">
                                        <el-option
                                            v-for="p in availableProducts"
                                            :key="p.id"
                                            :value="p.id"
                                            :label="p.name + (p.code ? ' (' + p.code + ')' : '')"/>
                                    </el-select>
                                </el-form-item>
                            </div>
                        </div>

                        <div class="modal-footer flex-center">
                            <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="form.loading">
                                <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                                <span v-if="form.loading" class="indicator-progress">
                                    {{ $t("messages.wait") }}
                                    <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            </button>
                        </div>
                    </el-form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";

export default {
    name: "ProductDependencyList",
    props: ['productID'],
    components: {CustomTable},
    data() {
        return {
            fields: [
                {name: this.$t("pages.ecommerce.product.dependency.cols.product_name"), scopedSlots: {customRender: "product_name"}},
                {name: this.$t("common.action"), key: "action", scopedSlots: {customRender: "actions"}},
            ],
            tableActions: [
                {name: "new", icon: "bi-plus-lg", label: "btn.add"},
                {name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh"},
                {name: "delete", icon: "bi-trash", label: "btn.delete"}
            ],
            selectedAndIds: [],
            selectedOrIds: [],
            form: {
                title: '',
                loading: false,
                pendingConditionId: 1,
                data: {},
            },
        }
    },
    computed: {
        table() {
            return this.$store.state.ecommerce.product.dependency.table;
        },
        andDependencies() {
            return (this.table.data || []).filter(d => d.condition_id === 1);
        },
        orDependencies() {
            return (this.table.data || []).filter(d => d.condition_id === 2);
        },
        allProducts() {
            return this.$store.state.ecommerce.product.table?.data || [];
        },
        availableProducts() {
            const usedIds = (this.table.data || []).map(d => d.depends_on_product_id);
            const selfId = Number(this.productID);
            return this.allProducts.filter(p => p.id !== selfId && !usedIds.includes(p.id));
        },
    },
    mounted() {
        this.refreshList();
        this.$store.dispatch('ecommerce/product/get', {filterData: {page_limit: 9999}});
    },
    methods: {
        refreshList() {
            this.$store.dispatch('ecommerce/product/dependency/get', {product_id: this.productID});
        },
        addDependency(conditionId) {
            this.resetForm();
            this.form.pendingConditionId = conditionId;
            this.form.title = conditionId === 1
                ? this.$t('pages.ecommerce.product.dependency.newRequiredTitle')
                : this.$t('pages.ecommerce.product.dependency.newAlternativeTitle');
            this.showModal(this.$refs.depModal);
        },
        deleteDependency(record) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('btn.delete'),
                message: this.$t('messages.sureDelete'),
                confirmType: 'danger',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/product/dependency/delete', {
                        id: record.id,
                        product_id: this.productID,
                    });
                    if (response?.status) {
                        this.onResponse(response, () => this.refreshList());
                    }
                },
            });
        },
        handleClickAction(name, conditionId) {
            switch (name) {
                case "new":
                    this.addDependency(conditionId);
                    break;
                case "refresh":
                    this.refreshList();
                    break;
                case "delete":
                    this.bulkDelete(conditionId);
                    break;
            }
        },
        handleAndSelected(record) {
            this.selectedAndIds = record;
        },
        handleOrSelected(record) {
            this.selectedOrIds = record;
        },
        async bulkDelete(conditionId) {
            const ids = (conditionId === 1 ? this.selectedAndIds : this.selectedOrIds).flat();
            if (!ids.length) {
                this.$message.warning(this.$t('messages.selectRow'));
                return;
            }
            for (const id of ids) {
                await this.$store.dispatch('ecommerce/product/dependency/delete', {
                    id,
                    product_id: this.productID,
                });
            }
            if (conditionId === 1) this.selectedAndIds = [];
            else this.selectedOrIds = [];
            this.refreshList();
        },
        onSubmit() {
            this.$refs.depForm.validate(async (valid) => {
                if (!valid) return false;

                this.form.loading = true;

                try {
                    const response = await this.$store.dispatch('ecommerce/product/dependency/add', {
                        product_id: this.productID,
                        formData: {
                            depends_on_product_id: this.form.data.depends_on_product_id,
                            condition_id: this.form.pendingConditionId,
                        },
                    });

                    if (response?.status) {
                        this.onResponse(response, () => {
                            this.hideModal(this.$refs.depModal);
                            this.refreshList();
                        });
                    }
                } finally {
                    this.form.loading = false;
                }
            });
        },
        resetForm() {
            this.form = {
                title: '',
                loading: false,
                pendingConditionId: 1,
                data: {
                    depends_on_product_id: undefined,
                },
            };
        },
    }
}
</script>
